/**
 * Acts: the structure a hundred flat rounds did not have.
 *
 * ## Why this file exists
 *
 * `docs/BALANCE.md` §7.1 measured a thirty-round plateau. A maxed twenty-four
 * slot board clears about 2.9 million effective hit points per round, the player
 * reaches that board around round 70, and rounds 70–100 therefore grow 0.6–0.8%
 * a round. No curve authoring fixes that, because the curve was never the
 * binding constraint — the *board* was, and the board's ceiling is
 *
 * ```
 * clearable RBE  ∝  slots × coverage × trackLength × abilityPower
 * ```
 *
 * Every term on the right is a property of the level, and until this file
 * existed all four were constants. An act changes all four at once. That is the
 * whole argument for acts: they are not a variety feature with a difficulty
 * side-effect, they are the difficulty fix, and variety is what falls out.
 *
 * ## The four terms, and where each number comes from
 *
 * - **slots** — authored per act, floored by geometry. `tests/levels.test.ts`
 *   asserts every act's budget is under its map's measured `reachableSlots`.
 * - **coverage** — *measured*, not guessed. `Track.meanCoverage(range, slots)`
 *   walks every buildable tile, scores it by how much of the centreline it can
 *   see, and averages the best `slots` of them. Expressed here relative to
 *   serpentine at twenty-four slots, which is the board `Balance.COVERAGE = 0.60`
 *   was calibrated against, so act IV reproduces the old model exactly and every
 *   other act moves away from it for a reason you can measure.
 * - **trackLength** — the centreline, in pixels. A round's demand is its hit
 *   points divided by the seconds it spends in front of the guns, and those
 *   seconds are `length / speed`. Maelstrom is 2,429px against outpost's 853, so
 *   the same group asks for a third of the DPS on the last map that it does on
 *   the first.
 * - **abilityPower** — the act rewards, folded to one scalar. See {@link ABILITIES}.
 *
 * ## The shape
 *
 * Seven acts, 12/12/16/20/15/13/12 rounds. They get *shorter* as the game goes
 * on, which is the opposite of the obvious layout and is deliberate: the last
 * forty rounds are where the plateau lived, so that is where the map, the
 * unlocks and the slot budget need to keep moving. An act is a promise that
 * something structural changes, and late game needs that promise kept often.
 *
 * Act finales land on 12, 24, 40, 60, 75, 88, 100. Three of those are boss
 * rounds (40, 60, 100); the bosses at 20 and 80 sit mid-act as a climax with
 * four rounds of act left to recover in.
 *
 * ## What this file does not do
 *
 * It holds no mutable run state. `Session.ts` owns which act is live and what
 * the player picked; this is the authored table and the pure lookups over it.
 */

import type { TrackMap } from './Track';
import { MAPS } from './Track';

// ───────────────────────────────────────────────────────────── abilities ──

/**
 * A commander ability, awarded at an act transition rather than bought.
 *
 * The brief asked for "a new axis rather than a bigger number", and the split
 * between `active` and `passive` is that axis. An active is a cooldown the
 * player has to spend attention on and can spend at the wrong moment; a passive
 * is a permanent statline the player never thinks about again. Every act offers
 * one of each, so the choice is genuinely about how you want to play rather
 * than about which number is larger.
 *
 * ## The model credits, which are guesses and say so
 *
 * `Balance.ts` cannot simulate a cooldown, so each ability declares what it is
 * worth as a multiplier on one of three quantities:
 *
 * - `dpsMul` — the board's sustained output.
 * - `windowMul` — the seconds a group spends under fire. Slows, stuns and
 *   freezes land here, which is also the one place the model gives control any
 *   credit at all (docs/BALANCE.md §7.1 item 3).
 * - `incomeMul` — cash. Converted to power at a square root, because doubling a
 *   player's money does not double their board: the upgrade tree is convex.
 *
 * These three are the same class of number as `Balance.COVERAGE` and
 * `Balance.WASTE`. They are stated, not derived, and {@link actPower} takes the
 * *weaker* of each act's two offers so the model never assumes the player picked
 * the strong one.
 */
export interface Ability {
  id: string;
  name: string;
  /** pixel-icon-library name, as everywhere else in the UI. */
  icon: string;
  blurb: string;
  kind: 'active' | 'passive';
  /** Seconds between uses. Zero for a passive. */
  cooldown: number;
  /** Seconds the effect lasts. Zero for a passive or an instant. */
  duration: number;
  dpsMul: number;
  windowMul: number;
  incomeMul: number;
}

const ability = (
  id: string, name: string, icon: string, kind: 'active' | 'passive',
  cooldown: number, duration: number,
  mul: { dps?: number; window?: number; income?: number },
  blurb: string,
): Ability => ({
  id, name, icon, kind, cooldown, duration, blurb,
  dpsMul: mul.dps ?? 1,
  windowMul: mul.window ?? 1,
  incomeMul: mul.income ?? 1,
});

/**
 * Twelve abilities, offered as six pairs. One pair per act transition, so a
 * player finishing the run holds six of the twelve and has refused six.
 *
 * The refusal is the point. Ten towers available from round one is why the
 * mid-game had no decisions left in it; twelve abilities available from round
 * one would be the same mistake with a different noun.
 */
export const ABILITIES: readonly Ability[] = [
  ability('overclock', 'Overclock', 'spinner-third-solid', 'active', 45, 6,
    { dps: 1.10 },
    'Every tower in the ring fires at eighty percent over rated for six seconds. Barrels are consumable.'),
  ability('entrench', 'Entrench', 'expand-solid', 'passive', 0, 0,
    { window: 1.08 },
    'Twelve percent more range on everything you own, permanently, for nothing.'),

  ability('siphon', 'Siphon Protocol', 'unlock-solid', 'active', 50, 8,
    { dps: 1.12 },
    'Eight seconds where armour is a suggestion. Half of every plate stops counting.'),
  ability('dragnet', 'Dragnet', 'grid-solid', 'passive', 0, 0,
    { window: 1.12 },
    'A permanent tar field over the whole corridor. Nothing crossing it keeps its speed.'),

  ability('prism', 'Prism Grid', 'shapes-solid', 'passive', 0, 0,
    { dps: 1.15 },
    'One extra pierce, one extra beam, one extra jump. Everywhere, forever.'),
  ability('writ', 'Bounty Writ', 'coins-solid', 'active', 60, 20,
    { income: 1.18 },
    'Twenty seconds where every pop pays double. The board you can afford is the board you get.'),

  ability('barrage', 'Barrage', 'table-solid', 'active', 55, 0,
    { dps: 1.15 },
    'Off-map artillery walks the length of the track once. You do not aim it.'),
  ability('staticfield', 'Static Field', 'times-square-solid', 'passive', 0, 0,
    { window: 1.15 },
    'The corridor holds a charge. Everything that can be stunned is, briefly, on the way in.'),

  ability('cascade', 'Cascade', 'share-alt-solid', 'active', 50, 8,
    { dps: 1.18 },
    'Eight seconds where a kill throws a fifth of its damage at whatever was standing closest.'),
  ability('foundry', 'Foundry', 'bank-solid', 'passive', 0, 0,
    { income: 1.14 },
    'Tier five costs a fifth less. The capstones stop being a fantasy.'),

  ability('focus', 'Focus Fire', 'eye-solid', 'passive', 0, 0,
    { dps: 1.12 },
    'Every tower picks the biggest thing in range and hits it a quarter harder.'),
  ability('stasis', 'Stasis Lock', 'lock-alt-solid', 'active', 60, 4,
    { window: 1.20 },
    'Four seconds where the track is a photograph. Boss bodies included.'),
];

const ABILITY_BY_ID = new Map<string, Ability>();
for (const a of ABILITIES) ABILITY_BY_ID.set(a.id, a);

export function abilityById(id: string): Ability {
  const a = ABILITY_BY_ID.get(id);
  if (!a) throw new Error(`unknown ability "${id}"`);
  return a;
}

/**
 * One ability's worth as a single scalar on the board's clearing power.
 *
 * Income enters at a square root. A player with twice the cash does not have
 * twice the board — the tree is convex, tier five costs five times what a naive
 * curve asks (docs/BALANCE.md §4.3), and the second half of a wallet buys much
 * less than the first.
 */
export function abilityPower(a: Ability): number {
  return a.dpsMul * a.windowMul * Math.sqrt(a.incomeMul);
}

// ───────────────────────────────────────────────────────────────── acts ──

export interface Act {
  /** 1-based. `ACTS[i].index === i + 1`. */
  index: number;
  name: string;
  blurb: string;
  /** First round of the act, inclusive. */
  first: number;
  /** Last round of the act, inclusive. */
  last: number;
  /** Key into `Track.MAPS`. */
  map: string;
  /** Centreline length in pixels. Asserted against the real geometry by the tests. */
  trackLength: number;
  /** Buildable slots the model assumes reachable. Floored by the map. */
  slots: number;
  /**
   * Fraction of a body's transit one tower sees, averaged over the best `slots`
   * tiles. Measured; see the header. Serpentine at 24 slots is 0.600 by
   * construction, which is `Balance.COVERAGE` unchanged.
   */
  coverage: number;
  /** Tower archetypes unlocked *at the start of* this act. Cumulative downward. */
  unlocks: readonly string[];
  /** The two abilities offered at the transition into this act. */
  offers: readonly string[];
  /** Enemy families this act is allowed to introduce. Gates `Waves.ROSTER`. */
  debuts: readonly string[];
}

/**
 * The seven acts.
 *
 * Map order is by centreline length, ascending, and that is not decoration: the
 * transit window is the denominator of every coverage number in the model, so a
 * longer map is directly a more forgiving one, and the acts have to get more
 * forgiving per tower at the same rate the rounds get bigger.
 *
 * Slot budgets climb 10 → 42. The old model's flat 24 is act IV, so everything
 * before this file's landing is reproduced exactly in the middle of the run and
 * moves in both directions from there.
 *
 * Acts VI and VII used to hold 38 and 48. Measured, that made the last twenty-five
 * rounds unloseable: `tests/simcheck.test.ts` fought act VII at three times its
 * authored RBE and did not drop a life, because a forty-eight tower board on a
 * 2,429px corridor removes anything the generator is allowed to put on it. The
 * budgets came down until the last two acts carried the run's bleeding, which is
 * where §7.4 always said it belonged. Cutting act VI alone to 30 was worse than
 * either extreme — it left act VI with six percent more clearing power than act
 * V against two and a half times the RBE, and the act fell off a cliff. An act
 * has to add capacity, and 34 → 42 is the smallest step that does.
 */
export const ACTS: readonly Act[] = [
  {
    index: 1,
    name: 'PROVING GROUND',
    blurb: 'One bend, one lobe, three towers. Learn what a tower is for.',
    first: 1, last: 12,
    map: 'outpost', trackLength: 853, slots: 10, coverage: 0.751,
    unlocks: ['turret', 'flak', 'mortar'],
    offers: [],
    debuts: ['grunt', 'runner', 'swarmling', 'grunt.heavy'],
  },
  {
    index: 2,
    name: 'THE COIL',
    blurb: 'A spiral that ends in the core. Everything comes to you, and the first boss with it.',
    first: 13, last: 24,
    map: 'vortex', trackLength: 1345, slots: 14, coverage: 0.766,
    unlocks: ['cryo', 'tesla'],
    offers: ['overclock', 'entrench'],
    debuts: ['armoured', 'weaver', 'splitter.prime', 'swarmling.mite'],
  },
  {
    index: 3,
    name: 'CROSSFIRE',
    blurb: 'A figure eight that crosses itself twice. Own the centre, own the act.',
    first: 25, last: 40,
    map: 'crossroads', trackLength: 1421, slots: 18, coverage: 0.780,
    unlocks: ['acid', 'laser'],
    offers: ['siphon', 'dragnet'],
    debuts: [
      'grunt.monolith', 'runner.blink', 'shielded', 'swarmling.hornet',
      'armoured.bulwark', 'weaver.phantom',
    ],
  },
  {
    index: 4,
    name: 'THE LONG COIL',
    blurb: 'Three lobes, sixteen hundred pixels. Every gap covers two strands.',
    first: 41, last: 60,
    map: 'serpentine', trackLength: 1574, slots: 24, coverage: 0.600,
    unlocks: ['pylon', 'sniper'],
    offers: ['prism', 'writ'],
    debuts: [
      'grunt.elite', 'shielded.warden', 'swarmling.drone',
      'runner.courier', 'weaver.mirage', 'armoured.siege',
    ],
  },
  {
    index: 5,
    name: 'CAUSEWAY',
    blurb: 'Four runs folded back on themselves. The middle band covers two at once.',
    first: 61, last: 75,
    map: 'causeway', trackLength: 1881, slots: 30, coverage: 0.477,
    unlocks: ['singularity'],
    offers: ['barrage', 'staticfield'],
    debuts: ['shielded.aegis', 'splitter.leviathan'],
  },
  {
    index: 6,
    name: 'LATTICEWORK',
    blurb: 'Six traversals, a hundred pixels apart. The whole board fires.',
    first: 76, last: 88,
    map: 'latticework', trackLength: 2059, slots: 34, coverage: 0.466,
    unlocks: [],
    offers: ['cascade', 'foundry'],
    debuts: ['armoured.citadel', 'runner.vector'],
  },
  {
    index: 7,
    name: 'MAELSTROM',
    blurb: 'In to the core and back out. Every radius is walked twice, and then it ends.',
    first: 89, last: 100,
    map: 'maelstrom', trackLength: 2429, slots: 42, coverage: 0.561,
    unlocks: [],
    offers: ['focus', 'stasis'],
    debuts: ['weaver.eclipse'],
  },
];

/** Round the whole act structure covers. Asserted against `WAVES.count`. */
export const TOTAL_ROUNDS = ACTS[ACTS.length - 1].last;

const ACT_OF_ROUND: Act[] = [];
for (const a of ACTS) for (let r = a.first; r <= a.last; r++) ACT_OF_ROUND[r] = a;

/** The act a round belongs to. Clamps outside the range rather than throwing. */
export function actOf(round: number): Act {
  const r = round < 1 ? 1 : round > TOTAL_ROUNDS ? TOTAL_ROUNDS : Math.floor(round);
  return ACT_OF_ROUND[r];
}

export function actByIndex(index: number): Act {
  const a = ACTS[index - 1];
  if (!a) throw new Error(`no act ${index}`);
  return a;
}

/** True on the last round of an act — the beat the transition follows. */
export function isActFinale(round: number): boolean {
  return actOf(round).last === round;
}

/** True on the first round of an act that is not the first act. */
export function isActOpening(round: number): boolean {
  const a = actOf(round);
  return a.first === round && a.index > 1;
}

/** Rounds an act runs for. */
export function actLength(a: Act): number {
  return a.last - a.first + 1;
}

/** Progress through the current act, 0 at its first round, 1 at its last. */
export function actProgress(round: number): number {
  const a = actOf(round);
  const n = actLength(a);
  return n <= 1 ? 1 : (round - a.first) / (n - 1);
}

// ────────────────────────────────────────────────────────────── unlocks ──

/**
 * Tower archetypes buildable on a round.
 *
 * Ten towers from round one is a large part of why the mid-game had no
 * decisions left in it: every problem already had its answer on the shelf, so
 * a new enemy family was a purchase rather than a rethink. Gating them means
 * `shielded` arrives at round 29 while Acid is still four rounds away, and the
 * player has to solve it with what they own.
 *
 * The schedule also has to keep `Balance.RECIPES` honest — a recipe whose
 * towers are not all unlocked is simply not offered on that round, so an act
 * that unlocked nothing useful would show up as a coverage hole rather than as
 * a design intention. See `Balance.recipesFor`.
 */
export function towersUnlockedAt(round: number): string[] {
  const act = actOf(round);
  const out: string[] = [];
  for (const a of ACTS) {
    if (a.index > act.index) break;
    for (const id of a.unlocks) out.push(id);
  }
  return out;
}

/**
 * The complement of {@link towersUnlockedAt}, which is the shape `ShopPanel`
 * wants: it takes a set of ids it must grey out, not a list it may show.
 */
export function towersLockedAt(round: number, all: readonly string[]): Set<string> {
  const open = new Set(towersUnlockedAt(round));
  const out = new Set<string>();
  for (const id of all) if (!open.has(id)) out.add(id);
  return out;
}

const UNLOCK_ROUND = new Map<string, number>();
for (const a of ACTS) for (const id of a.unlocks) UNLOCK_ROUND.set(id, a.first);

/** Round a tower becomes buildable, or `Infinity` if it never does. */
export function towerUnlockRound(id: string): number {
  return UNLOCK_ROUND.get(id) ?? Infinity;
}

/** Act index a tower unlocks in, or 0 if it is never unlocked. */
export function towerUnlockAct(id: string): number {
  const r = UNLOCK_ROUND.get(id);
  return r === undefined ? 0 : actOf(r).index;
}

/** Abilities offered at the transition *into* `act`. Empty for act 1. */
export function offersFor(act: Act): Ability[] {
  return act.offers.map(abilityById);
}

// ─────────────────────────────────────────────────────────── act power ──

/**
 * The ability multiplier the model assumes a player holds entering `round`.
 *
 * Conservative twice over. It takes the *weaker* of each act's two offers, and
 * it only counts acts the player has already finished — the pick made entering
 * act N is credited from act N's first round, never before.
 *
 * Landing at 1.81 by act VII means the model believes six ability picks are
 * worth about four fifths of a second board. That is the largest guess in this
 * file and the first number to distrust if the report starts looking generous.
 */
export function actPower(round: number): number {
  const act = actOf(round);
  let power = 1;
  for (const a of ACTS) {
    if (a.index > act.index) break;
    if (a.offers.length === 0) continue;
    let weakest = Infinity;
    for (const id of a.offers) weakest = Math.min(weakest, abilityPower(abilityById(id)));
    power *= weakest;
  }
  return power;
}

/**
 * Everything the solvability model needs to know about where a round is fought.
 * `Balance.checkRound` takes one of these instead of reading module constants.
 */
export interface Arena {
  act: number;
  map: string;
  slots: number;
  coverage: number;
  trackLength: number;
  power: number;
  unlocked: readonly string[];
}

export function arenaOf(round: number): Arena {
  const a = actOf(round);
  return {
    act: a.index,
    map: a.map,
    slots: a.slots,
    coverage: a.coverage,
    trackLength: a.trackLength,
    power: actPower(round),
    unlocked: towersUnlockedAt(round),
  };
}

/**
 * The act's board capacity relative to act IV — the twenty-four slot serpentine
 * the old flat model assumed. Clearing power is proportional to
 * `slots × coverage × trackLength × power`, so this one number is the whole
 * answer to "how much harder can this act's rounds be".
 *
 * It runs 0.27 → 5.06 across the seven acts. The thirty-round plateau existed
 * because it used to be 1.00 everywhere.
 */
export function capacityIndex(round: number): number {
  const a = actOf(round);
  const ref = ACTS[3];
  return (a.slots * a.coverage * a.trackLength * actPower(round))
    / (ref.slots * ref.coverage * ref.trackLength);
}

// ───────────────────────────────────────────────────────────── the map ──

/** The `TrackMap` an act is fought on. */
export function mapFor(round: number): TrackMap {
  const m = MAPS[actOf(round).map];
  if (!m) throw new Error(`act ${actOf(round).index} names unknown map "${actOf(round).map}"`);
  return m;
}

/** Map names in act order. Every entry must exist in `Track.MAPS`. */
export const ACT_MAPS: readonly string[] = ACTS.map((a) => a.map);
